import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { bookingService } from '../services/api';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import Avatar from './Avatar';
import { generateColorForAsset } from '../utils/colorUtils';
import { OPERATING_HOURS } from '../utils/constants';
import './Calendar.css';

function Calendar({
  user,
  assets,
  selectedAssetIds,
  setSelectedAssetIds,
  sidebarCollapsed,
  setSidebarCollapsed,
  equipmentType,
  setEquipmentType,
  uniqueEquipmentTypes,
  allBookings: centralizedBookings,
  dataLoading,
  dataError,
  isSocketConnected,
  onRefreshData,
  modalState,
  openModal,
  closeModal,
  minimizeModal,
  expandModal
}) {
  // Convert centralized booking data to FullCalendar events format
  const events = useMemo(() => {
    if (!centralizedBookings) return {};
    
    const now = new Date();
    
    const eventsByAsset = {};
    Object.keys(centralizedBookings).forEach(assetId => {
      const bookingsForAsset = centralizedBookings[assetId] || [];
      eventsByAsset[assetId] = bookingsForAsset.map(booking => {
        // Color events based on user ID instead of asset
        const userColorId = booking.extendedProps?.userId || booking.userId || 'default';
        
        // Check if booking is in the past
        const endDate = new Date(booking.end);
        const isPast = endDate < now || booking.isPast || booking.extendedProps?.isPast;
        
        // Preserve user colors for past bookings, let CSS handle the fading
        const baseColor = generateColorForAsset(userColorId);
        const eventColor = baseColor; // Always use the user's color, let CSS fade it
        
        return {
          ...booking,
          color: eventColor,
          backgroundColor: eventColor,
          borderColor: eventColor,
          textColor: '#fff',
          className: isPast ? 'past-booking-event' : ''
        };
      });
    });
    
    return eventsByAsset;
  }, [centralizedBookings]);

  // Responsive calendar view state
  const getCalendarView = () => {
    const w = window.innerWidth;
    if (w < 768) return 'timeGridDay'; // Changed from 'listWeek' to 'timeGridDay'
    if (w < 1024) return 'timeGridDay';
    return 'timeGridWeek';
  };
  
  const [calendarView, setCalendarView] = useState(getCalendarView());
  const calendarRef = useRef(null);
  const todayDate = new Date().toISOString().split('T')[0];
  
  // Get the start of the current week (today) for forward-looking view
  const getWeekStartDate = () => {
    return new Date(); // Always start from today
  };

  // Aggregate events across all selected assets
  const currentEvents = useMemo(() => {
    if (!selectedAssetIds || selectedAssetIds.length === 0) return [];
    
    const eventsMap = new Map(); // Use Map to prevent duplicates
    
    selectedAssetIds.forEach(assetId => {
      const assetEvents = events[assetId] || [];
      
      assetEvents.forEach(event => {
        // Use event ID as key to prevent duplicates
        if (!eventsMap.has(event.id)) {
          eventsMap.set(event.id, event);
        }
      });
    });
    
    return Array.from(eventsMap.values());
  }, [events, selectedAssetIds]);

  useEffect(() => {
    const handleResize = () => {
      const newView = getCalendarView();
      if (newView !== calendarView && calendarRef.current) {
        const api = calendarRef.current.getApi();
        api.changeView(newView);
        api.gotoDate(getWeekStartDate());
        setCalendarView(newView);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calendarView, todayDate]);

  // Simple useEffect to log centralized data usage
  useEffect(() => {
    console.log('Calendar using centralized data:', {
      totalAssets: Object.keys(events).length,
      selectedAssets: selectedAssetIds.length,
      isSocketConnected
    });
  }, [events, selectedAssetIds.length, isSocketConnected]);

  const openCreateModal = useCallback((start, end) => {
    if (selectedAssetIds.length === 0) {
      alert("Please select at least one asset from the sidebar to create a booking.");
      return;
    }
    
    // Ensure end time is always 2 hours after start time
    const startTime = new Date(start);
    const endTime = new Date(startTime.getTime() + (2 * 60 * 60 * 1000)); // Add 2 hours
    
    const primaryAssetIdForNewBooking = selectedAssetIds[0];
    openModal('create', {
      start: startTime,
      end: endTime,
      extendedProps: { 
        primaryAssetId: primaryAssetIdForNewBooking
      }
    });
  }, [selectedAssetIds, openModal]);

  const handleSelectSlot = ({ start, end }) => {
    openCreateModal(start, end);
  };

  // Handle single date/time clicks (alternative for mobile)
  const handleDateClick = ({ date, allDay }) => {
    if (!allDay) {
      // Create a 1-hour booking starting at the clicked time
      const start = date;
      const end = new Date(date.getTime() + 2 * 60 * 60 * 1000); // Add 1 hour
      openCreateModal(start, end);
    }
  };

  const handleEventClick = ({ event }) => {
    // Check if the booking is in the past
    const now = new Date();
    const endDate = new Date(event.end);
    const isPast = endDate < now || event.extendedProps?.isPast;
    
    const bookingData = {
      id: event.id,
      title: event.title,
      start: event.start,
      end: event.end,
      extendedProps: event.extendedProps
    };
    
    // Set mode based on whether booking is past or future
    openModal(isPast ? 'view' : 'edit', bookingData);
  };

  // Handle event drag (move) - prevent dragging past bookings
  const handleEventDrop = async (info) => {
    const { event } = info;
    
    // Check if the booking is in the past
    const now = new Date();
    const originalEndDate = new Date(info.oldEvent.end);
    const isPast = originalEndDate < now || info.oldEvent.extendedProps?.isPast;
    
    if (isPast) {
      alert('Cannot modify past bookings');
      info.revert();
      return;
    }
    
    // Validate the new event times against operating hours
    const startHour = event.start.getHours();
    const endHour = event.end.getHours();
    const endMinute = event.end.getMinutes();
    
    // Check if event is within operating hours
    if (startHour < OPERATING_HOURS.OPEN_HOUR ||
        (endHour > OPERATING_HOURS.CLOSE_HOUR || 
         (endHour === OPERATING_HOURS.CLOSE_HOUR && endMinute > 0))) {
      alert(`Bookings must be within operating hours: ${OPERATING_HOURS.OPEN_HOUR}:00 - ${OPERATING_HOURS.CLOSE_HOUR}:00`);
      info.revert();
      return;
    }
    
    try {
      // Preserve all booking data when updating time
      const updateData = {
        start: event.start,
        end: event.end,
        name: event.title,
        wingId: event.wingId || event.extendedProps?.wingId || null,
        foilId: event.foilId || event.extendedProps?.foilId || null,
        boardId: event.boardId || event.extendedProps?.boardId || null
      };
      
      await bookingService.update(event.id, updateData);
      // Data will be updated via socket events automatically
    } catch (err) {
      console.error('Failed to move booking:', err);
      const errorMessage = err.response?.data?.message || err.error || err.message || 'Unknown error';
      alert(`Failed to move booking: ${errorMessage}`);
      info.revert();
    }
  };

  // Handle event resize - prevent resizing past bookings
  const handleEventResize = async (info) => {
    const { event } = info;

    // Check if the booking is in the past
    const now = new Date();
    const originalEndDate = new Date(info.oldEvent.end);
    const isPast = originalEndDate < now || info.oldEvent.extendedProps?.isPast;
    
    if (isPast) {
      alert('Cannot modify past bookings');
      info.revert();
      return;
    }

    // Check for end time constraints
    const endHour = event.end.getHours();
    const endMinute = event.end.getMinutes();
    
    if (endHour > OPERATING_HOURS.CLOSE_HOUR || 
        (endHour === OPERATING_HOURS.CLOSE_HOUR && endMinute > 0)) {
      alert(`Bookings cannot extend past ${OPERATING_HOURS.CLOSE_HOUR}:00`);
      info.revert();
      return;
    }
    
    // Check minimum duration
    const duration = (event.end - event.start) / (1000 * 60);
    if (duration < OPERATING_HOURS.MIN_BOOKING_DURATION) {
      alert(`Bookings must be at least ${OPERATING_HOURS.MIN_BOOKING_DURATION} minutes long`);
      info.revert();
      return;
    }
    
    try {
      // Preserve all booking data when updating time
      const updateData = {
        start: event.start,
        end: event.end,
        name: event.title,
        wingId: event.wingId || event.extendedProps?.wingId || null,
        foilId: event.foilId || event.extendedProps?.foilId || null,
        boardId: event.boardId || event.extendedProps?.boardId || null
      };
      
      await bookingService.update(event.id, updateData);
      // Data will be updated via socket events automatically
    } catch (err) {
      console.error('Failed to resize booking:', err);
      const errorMessage = err.response?.data?.message || err.error || err.message || 'Unknown error';
      alert(`Failed to resize booking: ${errorMessage}`);
      info.revert();
    }
  };

  // Show loading state
  if (dataLoading) {
    return <div className="loading-container"><h2>Loading Calendar...</h2></div>;
  }

  // Show error state
  if (dataError) {
    return <div className="error-message">{dataError}</div>;
  }

  return (
    <div className="calendar-main-content">
      {/* FullCalendar component */}
      {selectedAssetIds.length > 0 || (assets.length > 0) ? (
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialDate={getWeekStartDate()}
          initialView={calendarView}
          locale="en-GB"
          headerToolbar={{
            left: 'prev,next',
            center: 'title',
            right: 'timeGridWeek,timeGridDay'
          }}
          buttonText={{
            timeGridWeek: 'Week',
            timeGridDay: 'Day'
          }}
          views={{
            timeGridWeek: {
              type: 'timeGrid',
              duration: { days: 7 },
              dayCount: 7,
              dateAlignment: 'day' // Align to the current date instead of week
            }
          }}
          height={600}
          events={currentEvents}
          selectable={true}
          selectMirror={true}
          selectMinDistance={5}
          longPressDelay={300}
          select={handleSelectSlot}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          editable={true}
          datesSet={(dateInfo) => {
            // When view changes, go to today's date
            if (dateInfo.view.type !== calendarView) {
              setCalendarView(dateInfo.view.type);
              setTimeout(() => {
                if (calendarRef.current) {
                  const api = calendarRef.current.getApi();
                  api.gotoDate(new Date());
                }
              }, 0);
            }
          }}
          eventContent={(arg) => {
            const { event } = arg;
            const extendedProps = event.extendedProps || {};
            
            // Collect all assets used in this booking
            const assetNames = [];
            
            // Check both direct properties and extendedProps for backward compatibility
            const wingId = event.wingId || extendedProps.wingId;
            const foilId = event.foilId || extendedProps.foilId;
            const boardId = event.boardId || extendedProps.boardId;
            
            // Helper to get asset name from booking data or fallback to asset lookup
            const getAssetNameForEvent = (assetId, assetType) => {
              // Check if this is a past booking
              const now = new Date();
              const endDate = new Date(event.end);
              const isPast = endDate < now || event.extendedProps?.isPast;
              
              // First try populated name from extendedProps
              const nameField = `${assetType}Name`;
              const deletedField = `${assetType}Deleted`;
              
              if (extendedProps[nameField]) {
                const assetName = extendedProps[nameField];
                const isDeleted = extendedProps[deletedField];
                
                // Priority: deleted > unavailable (only for past bookings)
                if (isPast && isDeleted) {
                  return `${assetName} - deleted`;
                }
                
                return assetName;
              }
              
              // Check if the asset is populated as an object in extendedProps
              const idField = `${assetType}Id`;
              const assetObj = extendedProps[idField];
              if (assetObj && typeof assetObj === 'object' && assetObj.name) {
                const assetName = assetObj.name;
                const objDeleted = assetObj.isDeleted;
                
                // Priority: deleted > unavailable (only for past bookings)
                if (isPast && objDeleted) {
                  return `${assetName} - deleted`;
                }
                
                return assetName;
              }
              
              // Fallback to asset lookup
              const asset = assets.find(a => a._id === assetId);
              if (asset) {
                // Priority: deleted > unavailable (only for past bookings)
                if (isPast && asset.isDeleted) {
                  return `${asset.name} - deleted`;
                }
                if (!asset.available) {
                  return `${asset.name} - unavailable`;
                }
                return asset.name;
              }
              return `${assetType.charAt(0).toUpperCase() + assetType.slice(1)} (ID: ${assetId})`;
            };
            
            if (wingId) {
              const assetName = getAssetNameForEvent(wingId, 'wing');
              if (assetName) assetNames.push(assetName);
            }
            
            if (foilId) {
              const assetName = getAssetNameForEvent(foilId, 'foil');
              if (assetName) assetNames.push(assetName);
            }
            
            if (boardId) {
              const assetName = getAssetNameForEvent(boardId, 'board');
              if (assetName) assetNames.push(assetName);
            }
            
            // Get user data for avatar display directly from booking data
            const userId = extendedProps.userId || event.userId;
            const userName = extendedProps.userName || event.title;
            const userAvatar = extendedProps.userAvatar;
            
            const userForAvatar = {
              id: userId,
              name: userName,
              avatar: userAvatar
            };
            
            return (
              <div className="fc-event-content">
                <div className="fc-event-header">
                  <div className="fc-event-title-and-avatar">
                    <div className="fc-event-title">{event.title}</div>
                    <div className="fc-event-avatar">
                      <Avatar 
                        user={userForAvatar} 
                        size={20} 
                      />
                    </div>
                  </div>
                </div>
                {assetNames.map((assetName, index) => (
                  <div key={index} className={`fc-event-asset ${assetName.includes('- deleted') ? 'deleted-asset' : ''}`}>
                    {assetName}
                  </div>
                ))}
              </div>
            );
          }}
          dayMaxEvents={true}
          weekends={true}
          slotMinTime="08:00:00"
          slotMaxTime="21:00:00"
          allDaySlot={false}
          slotDuration="00:30:00"
          slotLabelInterval="01:00:00"
          slotEventOverlap={false}
          businessHours={{
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
            startTime: `${String(OPERATING_HOURS.OPEN_HOUR).padStart(2, '0')}:00`,
            endTime: `${String(OPERATING_HOURS.CLOSE_HOUR).padStart(2, '0')}:00`
          }}
          selectConstraint="businessHours"
          eventConstraint="businessHours"
        />
      ) : (
        <div className="no-assets-selected">
          Please select equipment from the sidebar to view the calendar.
        </div>
      )}
    </div>
  );
}

export default Calendar;
